import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { PaymentMethodBrand } from 'webapp/enums/enums';
import { capitalize } from 'webapp/utils/common';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CommonUpgradePlanButtonComponent from 'webapp/components/common/upgrade-plan-button';
import SubmitButtonComponent from 'webapp/components/submit-button';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

interface Args {
	onChangePayment: () => void;
}

export default class BillingPaymentDetailsComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;

	get cardBrand() {
		const brand = this.billingService.paymentMethod?.brand || '';

		switch (brand) {
			case PaymentMethodBrand.AMEX:
			case PaymentMethodBrand.AMERICAN_EXPRESS:
				return 'amex';
			case PaymentMethodBrand.DISCOVER:
			case PaymentMethodBrand.DISCOVER_UPPERCASE:
				return 'discover';
			case PaymentMethodBrand.EFTPOS_AU:
				return 'eftpos';
			case PaymentMethodBrand.DINERS:
				return 'diners';
			case PaymentMethodBrand.LINK:
				return 'link';
			case PaymentMethodBrand.MAESTRO:
				return 'maestro';
			case PaymentMethodBrand.MASTER_CARD:
			case PaymentMethodBrand.MASTER_CARD_UPPERCASE:
				return 'mastercard';
			case PaymentMethodBrand.JCB:
				return 'jcb';
			case PaymentMethodBrand.UNION_PAY:
				return 'unionpay';
			case PaymentMethodBrand.VISA:
			case PaymentMethodBrand.VISA_UPPERCASE:
				return 'visa';
			case PaymentMethodBrand.UNKNOWN:
			default:
				return 'unknown';
		}
	}

	get cardInfoText() {
		const cardInfo = this.billingService.paymentMethod;
		if (cardInfo?.brand && cardInfo.expMonth && cardInfo.expYear) {
			return `${capitalize(cardInfo.brand.toString())} - Expires ${cardInfo.expMonth}/${cardInfo.expYear.toString().substring(2)}`;
		} else {
			return '';
		}
	}

	get showEditBtn() {
		return (
			this.billingService.hasPayment &&
			!this.billingService.isPayingViaInvoice &&
			!!this.billingService.subscription
		);
	}

	get showNextBillingDate() {
		return (
			!this.billingService.activePlan?.isFree &&
			(this.billingService.isActive ||
				(this.billingService.isActiveTrial && this.billingService.hasPayment))
		);
	}

	get availablePaymentMethodsTooltip() {
		const htmlContent = `
    	    <div class="wrapper">
    	        <strong>Accepted payment methods</strong><br>
    	        We are accepting: Visa, MasterCard, Maestro, American Express, JCB, Discover, Diners Club, Union Pay, Google Pay, Apple Pay. Annual subscriptions can be billed with other payment methods (wire bank transfer, checks,...).
				<br><br>
				Contact us for more information.
    	    </div>
    	`;

		return htmlContent;
	}

	get showPaymentBtn() {
		return (
			this.billingService.isTrialWithoutPayment ||
			this.billingService.isPaymentError ||
			this.billingService.isCanceled ||
			this.billingService.isExpired ||
			this.hasSubscriptionWithoutPayment
		);
	}

	get paymentBtnText() {
		return `${this.billingService.hasPayment ? 'Update' : 'Add'} payment information`;
	}

	get showBillingPeriod() {
		return (
			!this.billingService.isCanceled &&
			!this.billingService.activePlan?.isFree &&
			!this.billingService.isExpiredTrial
		);
	}

	get hasSubscriptionWithoutPayment() {
		return this.showBillingPeriod && !this.billingService.hasPayment;
	}

	get showScheduledBillingChange() {
		return (
			this.billingService.activePlan?.billingCycle !==
			this.billingService.finalPlan?.billingCycle
		);
	}

	get activeCommitmentTooltipText() {
		return `Your subscription is committed through this date. Cancellation becomes available ${this.billingService.commitmentActionDateOffsetDays} days before the commitment ends.`;
	}

	@action
	editPaymentInfo() {
		this.billingService.changePayment();
	}

	@action
	changeBillingCycle() {
		if (this.billingService.isActiveTrial) {
			this.navigationService.transitionToRoute('settings.billing.plans');
		} else {
			const plan = this.billingService?.finalPlan;
			if (plan) {
				this.billingService.switchPlan(plan, { billingPeriodChangePopup: 'billing_page' });
			}
		}
	}
	<template>
		<div class="payment-details">
			<div class="payment-details__header">
				<div class="topline">
					<span>Payment details</span>
					{{#if this.showEditBtn}}
						<a role="button" {{on "click" this.editPaymentInfo}}>Edit</a>
					{{/if}}
				</div>
			</div>

			<div class="payment-details__content">

				{{#if this.billingService.hasPayment}}
					{{#if this.billingService.isPayingWithCreditCard}}
						<div class="payment-type payment-type--card">
							<div class="card__header">
								<img
									src="/images/billing/{{this.cardBrand}}.svg"
									width="48"
									height="auto"
									alt="Card brand"
								/>
								<div class="card__number">
									<span class="dots"></span>
									<span class="dots"></span>
									<span class="dots"></span>
									<span class="last4">
										{{this.billingService.paymentMethod.last4}}
									</span>
								</div>
							</div>
							<div class="card__info">
								{{this.cardInfoText}}
							</div>
						</div>
					{{else if this.billingService.isPayingViaInvoice}}
						<div class="payment-type">
							<span>Billing payment method set to <strong>Invoice</strong>.</span>
						</div>
					{{else if this.billingService.isPayingWithBankAccount}}
						<div class="payment-type">
							<span>
								Billing payment method set to
								<strong>Bank account</strong>
								({{this.billingService.paymentMethod.fullName}}).
							</span>
						</div>
					{{else}}
						<div class="payment-type">
							<span>
								Billing payment method set to
								<strong>{{this.billingService.paymentMethod.type}}</strong>.
							</span>
						</div>
					{{/if}}
				{{/if}}

				{{#if this.showBillingPeriod}}
					<p>
						Billing period is set to
						<strong>{{this.billingService.activePlan.billingCycleTextLong}}</strong>.
						<a {{on "click" this.changeBillingCycle}}>Change</a>
					</p>
				{{/if}}

				{{#if this.showScheduledBillingChange}}
					<p>
						<CommonUiIconComponent @name="AlertFill" @size="sm" />
						Scheduled to switch to
						<strong>{{this.billingService.finalPlan.billingCycleTextLong}}</strong>
						on
						<em>{{this.billingService.formattedActiveUntil}}</em>.
					</p>
				{{/if}}

				{{#if this.showNextBillingDate}}
					<p>
						Next invoice on
						<strong>{{this.billingService.formattedNextPaymentDate}}</strong>.
					</p>
				{{/if}}

				{{#if this.billingService.hasActiveCommitment}}
					<p class="commitment">
						Commitment through
						<strong>{{this.billingService.commitmentUntilFormatted}}</strong>.
						<ToolTipWithIconComponent
							@text={{this.activeCommitmentTooltipText}}
							@maxWidth={{230}}
						/>
					</p>
				{{/if}}

				{{#unless this.billingService.hasPayment}}
					<p>
						Available payment methods (and others
						<ToolTipWithIconComponent
							@text={{this.availablePaymentMethodsTooltip}}
							@maxWidth={{250}}
						/>):
					</p>
					<ul>
						<li>
							<img src="/images/billing/mastercard.svg" alt />
						</li>
						<li>
							<img src="/images/billing/visa.svg" alt />
						</li>
						<li>
							<img src="/images/billing/amex.svg" alt />
						</li>
						<li>
							<img src="/images/billing/discover.svg" alt />
						</li>
						<li>
							<img src="/images/billing/jcb.svg" alt />
						</li>
					</ul>
				{{/unless}}
			</div>

			{{#if this.showPaymentBtn}}
				<div class="payment-details__controls">
					<SubmitButtonComponent
						@text={{this.paymentBtnText}}
						@extraClass="save"
						@onTrigger={{@onChangePayment}}
					/>
				</div>
			{{else if this.billingService.activePlan.isFree}}
				<div class="payment-details__controls">
					<CommonUpgradePlanButtonComponent
						@text="Update plan"
						@trackingOrigin="billing_page"
					/>
				</div>
			{{/if}}
		</div>
	</template>
}
